<template>
  <PageChrome>
    <template #title>
      <span class="eyebrow">Profile</span>
    </template>

    <template #actions>
      <span v-if="pageSaved || editorRef?.savedAt" class="autoSaveIndicator saved">Saved</span>
    </template>

    <div class="profilePage">
      <div v-if="loading" class="hint">Loading...</div>
      <div v-else-if="!currentUserEmail" class="hint">
        Unable to determine your email address.
      </div>
      <template v-else>
        <div class="profileHeader">
          <h2 class="profileTitle">{{ isDialerOnly ? 'Your Profile' : 'Your Sender Profile' }}</h2>
          <div class="profileEmail">{{ currentUserEmail }}</div>
        </div>

        <template v-if="!isDialerOnly">
          <!-- Enable sending toggle -->
          <label class="checkboxRow">
            <input type="checkbox" v-model="sendingEnabled" @change="onToggleSending" :disabled="toggling" />
            <span>Enable sending messages from this mailbox</span>
          </label>
          <div class="hint" style="margin:0 0 8px;">When enabled, agents can send emails from your address.</div>

          <SenderIdentityEditor
            ref="editorRef"
            :senderEmail="currentUserEmail"
            mode="personal"
          />
        </template>

        <CrmUserIdentity />
      </template>
    </div>
  </PageChrome>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import PageChrome from '../components/PageChrome.vue'
import SenderIdentityEditor from '../components/SenderIdentityEditor.vue'
import CrmUserIdentity from '../components/CrmUserIdentity.vue'
import { apiFetch } from '../../services/api'
import { useCurrentUser } from '../../services/user'

const { isDialerOnly } = useCurrentUser()

const editorRef = ref<InstanceType<typeof SenderIdentityEditor> | null>(null)
const currentUserEmail = ref('')
const loading = ref(true)
const sendingEnabled = ref(false)
const toggling = ref(false)
const pageSaved = ref(false)
let pageSavedTimer: ReturnType<typeof setTimeout> | null = null

onMounted(async () => {
  try {
    const me = await apiFetch('/auth/me')
    const email = me?.email || ''
    if (email) {
      currentUserEmail.value = email
      // Dialer-only has no send path — the email comes from /auth/me alone and
      // the sender-identity machinery (and its fetches) never loads.
      if (!isDialerOnly.value) {
        // Check if a sender identity already exists for this email
        const settings = await apiFetch('/settings/email')
        const identities = settings?.sender_identities || []
        const myIdentity = identities.find(
          (i: any) => (i.email || '').toLowerCase() === email.toLowerCase()
        )
        sendingEnabled.value = Boolean(myIdentity?.enabled)
      }
    }
  } catch {
    if (!isDialerOnly.value) {
      try {
        const settings = await apiFetch('/settings/email')
        const identities = settings?.sender_identities || []
        if (identities.length === 1) {
          currentUserEmail.value = identities[0].email
          sendingEnabled.value = true
        }
      } catch {
        // ignore
      }
    }
  } finally {
    loading.value = false
  }
})

async function onToggleSending() {
  toggling.value = true
  try {
    await apiFetch(`/signatures/sender-identity/${encodeURIComponent(currentUserEmail.value.toLowerCase())}`, {
      method: 'PATCH',
      body: { enabled: sendingEnabled.value },
    })
    pageSaved.value = true
    if (pageSavedTimer) clearTimeout(pageSavedTimer)
    pageSavedTimer = setTimeout(() => { pageSaved.value = false }, 2000)
  } catch (e: any) {
    console.error('Failed to toggle sending:', e)
    sendingEnabled.value = !sendingEnabled.value
  } finally {
    toggling.value = false
  }
}
</script>

<style scoped>
.profilePage {
  max-width: 600px;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.profileHeader {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 12px;
}

.profileTitle {
  font-family: var(--font-display);
  font-size: var(--fs-lg, 24px);
  font-weight: var(--fw-semibold, 600);
  letter-spacing: var(--tracking-tight, -0.01em);
  line-height: var(--lh-tight, 1.2);
  color: var(--color-text-primary, var(--text));
  margin: 0;
}

.profileEmail {
  font-family: var(--font-sans, sans-serif);
  font-size: var(--fs-sm, 13px);
  font-weight: var(--fw-medium, 500);
  color: var(--color-text-secondary, var(--muted));
}

.hint {
  color: var(--muted);
  font-size: 12px;
  line-height: 1.5;
}

.checkboxRow {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
  color: var(--color-text-primary, var(--text));
  cursor: pointer;
  margin-bottom: 4px;
}

.autoSaveIndicator.saved {
  font-size: 13px;
  font-weight: 600;
  color: var(--success, #059669);
}
</style>
